import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Sparkles, Plus, Save, Trash2, Printer, ArrowLeft, RefreshCw, Eye, EyeOff, X,
  FolderPlus, Pencil, Lock,
} from 'lucide-react';

const EMPTY_COLLECTION = {
  slug: '',
  emoji: '✨',
  icon: 'Sparkles',
  name: '',
  tagline: '',
  style: '',
  colors: [],
  designs: [],
  mood: [],
  styling_tip: '',
  palette: { bg: '#F5EDE4', accent: '#C4A084', text: '#5D4E3C' },
  is_active: true,
  sort_order: 0,
  product_skus: [],
  category: 'limited-hair',
};

const EMPTY_CATEGORY = {
  slug: '',
  name: '',
  tagline: '',
  description: '',
  emoji: '✨',
  accent_color: '#C4A084',
  sort_order: 99,
  is_active: true,
};

const slugify = (s) => s.toLowerCase()
  .replace(/[äöüß]/g, (ch) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[ch] || ch))
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

const TagInput = ({ label, value, onChange, placeholder, testId }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div>
      <Label className="text-xs text-[#5D4E3C]">{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={testId}
        />
        <Button type="button" variant="outline" size="sm" onClick={add}>+</Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#F5EDE4] border border-[#C4A084] text-[#5D4E3C]">
            {v}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="hover:text-red-600">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

const CategoryModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    ...EMPTY_CATEGORY,
    ...initial,
  }));
  const isEdit = Boolean(initial?.slug);
  const isProtected = Boolean(initial?.is_protected);

  const submit = async () => {
    const slug = (form.slug || slugify(form.name)).toLowerCase();
    if (!slug || !form.name) {
      toast.error('Name und Slug sind Pflicht');
      return;
    }
    try {
      if (isEdit) {
        await axiosInstance.put(`/hair-collections/categories/${slug}`, { ...form, slug });
        toast.success('Kategorie gespeichert');
      } else {
        await axiosInstance.post('/hair-collections/categories', { ...form, slug });
        toast.success('Kategorie erstellt');
      }
      onSaved(slug);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="category-modal">
      <div className="bg-white max-w-lg w-full p-6 border border-[#C4A084]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
            {isEdit ? `Kategorie bearbeiten${isProtected ? ' (geschützt)' : ''}` : 'Neue Kategorie'}
          </h2>
          <button onClick={onClose} className="text-[#9C8C7A] hover:text-[#5D4E3C]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5D4E3C]">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({
                  ...form,
                  name: e.target.value,
                  slug: isEdit ? form.slug : slugify(e.target.value),
                })}
                placeholder="z.B. Saison Drops"
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5D4E3C]">Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                disabled={isEdit}
                className="font-mono text-sm"
                data-testid="category-slug-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5D4E3C]">Emoji</Label>
              <Input
                value={form.emoji}
                onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                maxLength={4}
              />
            </div>
            <div>
              <Label className="text-xs text-[#5D4E3C]">Akzentfarbe</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="h-10 w-12 border border-input"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs text-[#5D4E3C]">Tagline (Untertitel)</Label>
            <Input
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="Kurzer Slogan"
            />
          </div>

          <div>
            <Label className="text-xs text-[#5D4E3C]">Beschreibung</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Wird oben in der Übersicht angezeigt"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label className="text-sm text-[#5D4E3C]">Aktiv</Label>
            </div>
            <div>
              <Label className="text-xs text-[#5D4E3C]">Sortierung</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} className="bg-[#5D4E3C] hover:bg-[#4A3E30] text-white" data-testid="category-save-btn">
            <Save className="h-4 w-4 mr-2" /> Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

const HairCollectionsManager = () => {
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('limited-hair');
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [icons, setIcons] = useState([]);
  const [categoryModal, setCategoryModal] = useState(null); // null | EMPTY | category

  const load = async () => {
    setLoading(true);
    try {
      const [colRes, iconRes, catRes] = await Promise.all([
        axiosInstance.get('/hair-collections?include_inactive=true'),
        axiosInstance.get('/hair-collections/icons'),
        axiosInstance.get('/hair-collections/categories?include_inactive=true'),
      ]);
      setCollections(colRes.data || []);
      setIcons(iconRes.data?.icons || []);
      const cats = catRes.data || [];
      setCategories(cats);
      // If active tab no longer exists, jump to first
      if (cats.length && !cats.find((c) => c.slug === activeCategory)) {
        setActiveCategory(cats[0].slug);
      }
    } catch (e) {
      toast.error('Konnte Daten nicht laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visibleCollections = useMemo(
    () => collections.filter((c) => (c.category || 'limited-hair') === activeCategory),
    [collections, activeCategory],
  );

  const startEdit = (c) => {
    setIsNew(false);
    setEditing(JSON.parse(JSON.stringify({ ...EMPTY_COLLECTION, ...c })));
  };

  const startNew = () => {
    setIsNew(true);
    setEditing({
      ...EMPTY_COLLECTION,
      category: activeCategory,
      sort_order: (visibleCollections[visibleCollections.length - 1]?.sort_order || 0) + 1,
    });
  };

  const save = async () => {
    if (!editing.slug || !editing.name) {
      toast.error('Slug und Name sind Pflicht');
      return;
    }
    try {
      if (isNew) {
        await axiosInstance.post('/hair-collections', editing);
        toast.success('Kollektion erstellt');
      } else {
        await axiosInstance.put(`/hair-collections/${editing.slug}`, editing);
        toast.success('Kollektion gespeichert');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Kollektion "${slug}" wirklich löschen?`)) return;
    try {
      await axiosInstance.delete(`/hair-collections/${slug}`);
      toast.success('Gelöscht');
      load();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  // Ein-Klick-Toggle: aktiviert / deaktiviert eine Kollektion ohne das Edit-Formular zu öffnen.
  // Zusätzlicher Bonus: beim Aktivieren einer Jahreszeit-Edition werden die anderen 3 automatisch deaktiviert
  // (kann via Confirm-Dialog übersprungen werden — eine einzelne Jahreszeit aktiv zu haben ist Best Practice).
  const toggleActive = async (collection) => {
    const target = !(collection.is_active !== false);
    const isSeasonal = (collection.category || 'limited-hair') === 'limited-seasonal';
    let deactivateOthers = false;
    if (target && isSeasonal) {
      const others = collections.filter(
        (c) => (c.category || 'limited-hair') === 'limited-seasonal' && c.slug !== collection.slug && c.is_active !== false
      );
      if (others.length > 0) {
        deactivateOthers = window.confirm(
          `„${collection.name}" als aktive Jahreszeit setzen?\n\n` +
          `Die anderen ${others.length} Jahreszeit-Edition${others.length === 1 ? '' : 'en'} ` +
          `(${others.map((o) => o.name).join(', ')}) werden dabei deaktiviert.\n\n` +
          `OK = nur diese aktivieren · Abbrechen = nichts tun.`
        );
        if (!deactivateOthers) return;
      }
    }
    try {
      const payload = { ...collection, is_active: target };
      delete payload._id;
      await axiosInstance.put(`/hair-collections/${collection.slug}`, payload);
      if (deactivateOthers) {
        const others = collections.filter(
          (c) => (c.category || 'limited-hair') === 'limited-seasonal' && c.slug !== collection.slug && c.is_active !== false
        );
        await Promise.all(
          others.map((o) => axiosInstance.put(`/hair-collections/${o.slug}`, { ...o, is_active: false }))
        );
        toast.success(`„${collection.name}" aktiv – ${others.length} andere Edition${others.length === 1 ? '' : 'en'} deaktiviert`);
      } else {
        toast.success(target ? `„${collection.name}" aktiviert` : `„${collection.name}" deaktiviert`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status-Wechsel fehlgeschlagen');
    }
  };

  const seed = async () => {
    if (!window.confirm('Standardthemen für ALLE Kategorien anlegen / aktualisieren?\n\n(Hair · Seasonal · Beauty · Home · Study)\n\nBestehende Themen werden idempotent aktualisiert.')) return;
    try {
      const r = await axiosInstance.post('/hair-collections/seed');
      const d = r.data?.details || {};
      toast.success(
        `✓ ${r.data.inserted} neu · ${r.data.updated} aktualisiert\n` +
        `Hair ${d.hair?.inserted ?? 0}/${d.hair?.updated ?? 0} · ` +
        `Seasonal ${d.seasonal?.inserted ?? 0}/${d.seasonal?.updated ?? 0} · ` +
        `Beauty+Home ${d.beauty_home?.inserted ?? 0}/${d.beauty_home?.updated ?? 0} · ` +
        `Study ${d.study?.inserted ?? 0}/${d.study?.updated ?? 0}`,
        { duration: 6000 }
      );
      load();
    } catch (e) {
      toast.error('Seed fehlgeschlagen');
    }
  };

  const downloadLabel = async (slug, box_type, layout = 'a4_sheet', gift_label_text = '') => {
    try {
      const qs = new URLSearchParams({ box_type, layout });
      if (gift_label_text) qs.set('gift_label_text', gift_label_text.slice(0, 30));
      const r = await axiosInstance.get(
        `/hair-collections/${slug}/label-pdf?${qs.toString()}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const personalised = gift_label_text ? `-${gift_label_text.replace(/\s+/g, '_').slice(0, 16)}` : '';
      a.download = `labels-${slug}-${box_type}-${layout}${personalised}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF-Erstellung fehlgeschlagen');
    }
  };

  const downloadPersonalisedLabel = (slug, box_type) => {
    const txt = window.prompt(
      'Empfänger-Name oder Botschaft für das Etikett (max 30 Zeichen)\n\nz.B. "Für Anna" oder "Frohe Weihnachten"\n\nLeer lassen → Standard-Etikett',
      '',
    );
    if (txt === null) return; // cancelled
    downloadLabel(slug, box_type, 'single', txt.trim());
  };

  const deleteCategory = async (cat) => {
    if (cat.is_protected) {
      toast.error('Geschützte Kategorie kann nicht gelöscht werden.');
      return;
    }
    if (!window.confirm(`Kategorie "${cat.name}" wirklich löschen?\nDas geht nur, wenn keine Kollektionen mehr in dieser Kategorie sind.`)) return;
    try {
      await axiosInstance.delete(`/hair-collections/categories/${cat.slug}`);
      toast.success('Kategorie gelöscht');
      if (activeCategory === cat.slug) setActiveCategory('limited-hair');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  if (loading && !editing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <p className="text-[#8B7355]">Lädt …</p>
      </div>
    );
  }

  // ====== EDITOR VIEW ======
  if (editing) {
    const p = editing.palette;
    return (
      <div className="min-h-screen py-6 px-4" style={{ backgroundColor: '#F5EDE4' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-2 text-sm text-[#5D4E3C] hover:opacity-70"
              data-testid="hair-collection-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
            </button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/collections/${editing.slug}`, '_blank')}
                disabled={isNew}
              >
                <Eye className="h-4 w-4 mr-2" /> Vorschau im Shop
              </Button>
              <Button onClick={save} className="bg-[#5D4E3C] hover:bg-[#4A3E30] text-white" data-testid="hair-collection-save-btn">
                <Save className="h-4 w-4 mr-2" /> Speichern
              </Button>
            </div>
          </div>

          <div className="bg-white p-6 border border-[#C4A084] mb-5">
            <h1 className="text-2xl font-bold text-[#5D4E3C] mb-1" style={{ fontFamily: 'Georgia, serif' }}>
              {isNew ? 'Neue Kollektion' : `Kollektion bearbeiten: ${editing.name}`}
            </h1>
            <p className="text-sm text-[#8B7355]">Texte, Farben und Symbole werden sofort im Shop sichtbar.</p>
          </div>

          {/* Live preview tile */}
          <div className="mb-5">
            <Label className="text-xs text-[#8B7355] mb-2 block">Vorschau-Kachel</Label>
            <div
              className="relative aspect-[4/3] max-w-md p-6 overflow-hidden"
              style={{ backgroundColor: p.bg, color: p.text }}
            >
              <div className="text-3xl mb-1">{editing.emoji}</div>
              <p className="text-[10px] tracking-[3px] uppercase font-semibold" style={{ color: p.accent }}>
                {editing.style || 'Stil'}
              </p>
              <h3 className="text-2xl font-bold mt-1" style={{ fontFamily: 'Georgia, serif' }}>
                {editing.name || 'Kollektions-Name'}
              </h3>
              <p className="text-xs mt-2 opacity-80">{editing.tagline || 'Tagline'}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {editing.colors.slice(0, 4).map((col) => (
                  <span key={col} className="text-[10px] px-2 py-0.5" style={{
                    backgroundColor: `${p.accent}22`, color: p.text, border: `1px solid ${p.accent}55`,
                  }}>{col}</span>
                ))}
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="bg-white p-6 border border-[#C4A084] space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-[#5D4E3C]">Slug (URL) *</Label>
                <Input
                  value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                  disabled={!isNew}
                  placeholder="z.B. blossom-delight"
                  data-testid="hair-collection-slug-input"
                />
                <p className="text-[10px] text-[#9C8C7A] mt-1">Nach dem Anlegen nicht mehr änderbar.</p>
              </div>
              <div>
                <Label className="text-xs text-[#5D4E3C]">Name *</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setEditing({
                      ...editing,
                      name,
                      slug: isNew && !editing.slug ? slugify(name) : editing.slug,
                    });
                  }}
                  placeholder="z.B. Blossom Delight"
                  data-testid="hair-collection-name-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-[#5D4E3C]">Kategorie (Tab) *</Label>
              <select
                value={editing.category || 'limited-hair'}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="w-full border border-input px-3 py-2 text-sm rounded-md bg-white"
                data-testid="hair-collection-category-select"
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.emoji} {c.name}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-[#9C8C7A] mt-1">Bestimmt, in welchem Tab die Kollektion im Shop erscheint.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-[#5D4E3C]">Emoji</Label>
                <Input
                  value={editing.emoji}
                  onChange={(e) => setEditing({ ...editing, emoji: e.target.value })}
                  maxLength={4}
                  placeholder="🌸"
                />
                <p className="text-[10px] text-[#9C8C7A] mt-1">Wird auf der Webseite & im PDF-Label gezeigt.</p>
              </div>
              <div>
                <Label className="text-xs text-[#5D4E3C]">Lucide-Icon (optional)</Label>
                <select
                  value={editing.icon || ''}
                  onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                  className="w-full border border-input px-3 py-2 text-sm rounded-md"
                  data-testid="hair-collection-icon-select"
                >
                  <option value="">— ohne —</option>
                  {icons.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-[#5D4E3C]">Stil</Label>
                <Input
                  value={editing.style}
                  onChange={(e) => setEditing({ ...editing, style: e.target.value })}
                  placeholder="Kawaii / Glam / Boho ..."
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-[#5D4E3C]">Tagline</Label>
              <Input
                value={editing.tagline}
                onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                placeholder="Kurzer Slogan, max. 60 Zeichen"
              />
            </div>

            <div>
              <Label className="text-xs text-[#5D4E3C]">Styling-Tipp</Label>
              <Textarea
                value={editing.styling_tip}
                onChange={(e) => setEditing({ ...editing, styling_tip: e.target.value })}
                rows={2}
                placeholder="Wann/zu was passt diese Kollektion?"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TagInput
                label="Farben"
                value={editing.colors}
                onChange={(v) => setEditing({ ...editing, colors: v })}
                placeholder="z.B. Rosa"
                testId="hair-collection-colors-input"
              />
              <TagInput
                label="Designs"
                value={editing.designs}
                onChange={(v) => setEditing({ ...editing, designs: v })}
                placeholder="z.B. Herzen"
                testId="hair-collection-designs-input"
              />
              <TagInput
                label="Wirkung / Mood"
                value={editing.mood}
                onChange={(v) => setEditing({ ...editing, mood: v })}
                placeholder="z.B. Verspielt"
                testId="hair-collection-mood-input"
              />
            </div>

            <div>
              <Label className="text-xs text-[#5D4E3C]">Farb-Palette</Label>
              <div className="grid grid-cols-3 gap-3 mt-1">
                {['bg', 'accent', 'text'].map((k) => (
                  <div key={k}>
                    <p className="text-[10px] text-[#9C8C7A] uppercase tracking-wider mb-1">
                      {k === 'bg' ? 'Hintergrund' : k === 'accent' ? 'Akzent' : 'Text'}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={p[k]}
                        onChange={(e) => setEditing({ ...editing, palette: { ...p, [k]: e.target.value } })}
                        className="h-10 w-12 border border-input"
                        data-testid={`hair-collection-color-${k}`}
                      />
                      <Input
                        value={p[k]}
                        onChange={(e) => setEditing({ ...editing, palette: { ...p, [k]: e.target.value } })}
                        className="text-sm font-mono"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#E8DCC8]">
              <div className="flex items-center gap-3">
                <Switch
                  checked={editing.is_active}
                  onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                  data-testid="hair-collection-active-switch"
                />
                <Label className="text-sm text-[#5D4E3C]">Aktiv (im Shop sichtbar)</Label>
              </div>
              <div>
                <Label className="text-xs text-[#5D4E3C]">Sortierung</Label>
                <Input
                  type="number"
                  value={editing.sort_order}
                  onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                  className="text-sm"
                />
              </div>
            </div>

            {editing.product_skus?.length > 0 && (
              <div className="pt-2 border-t border-[#E8DCC8]">
                <Label className="text-xs text-[#5D4E3C]">Verknüpfte Boxen (Produkte)</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {editing.product_skus.map((sku) => (
                    <span key={sku} className="text-[11px] px-2 py-1 bg-[#F5EDE4] border border-[#C4A084] text-[#5D4E3C] font-mono">
                      {sku}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ====== LIST VIEW ======
  const activeCat = categories.find((c) => c.slug === activeCategory);

  return (
    <div className="min-h-screen py-6 px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#5D4E3C] flex items-center gap-2" style={{ fontFamily: 'Georgia, serif' }}>
              <Sparkles className="h-6 w-6 text-[#C4A084]" /> Kollektionen
            </h1>
            <p className="text-sm text-[#8B7355]">Themen, Tabs & PDF-Labels für alle Kollektionsboxen verwalten</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setCategoryModal({ ...EMPTY_CATEGORY })} data-testid="new-category-btn">
              <FolderPlus className="h-4 w-4 mr-2" /> Neue Kategorie
            </Button>
            <Button variant="outline" size="sm" onClick={seed} data-testid="hair-collection-seed-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Standard-Themen {collections.length === 0 ? 'anlegen' : 'wiederherstellen'}
            </Button>
            <Button onClick={startNew} className="bg-[#5D4E3C] hover:bg-[#4A3E30] text-white" data-testid="hair-collection-new-btn">
              <Plus className="h-4 w-4 mr-2" /> Neue Kollektion
            </Button>
          </div>
        </div>

        {/* Category Tabs — mobile: scroll-snap row · md+: standard tabs */}
        <div
          className="flex items-stretch gap-2 mb-5 border-b border-[#E8DCC8] overflow-x-auto pb-px scroll-smooth snap-x snap-mandatory"
          data-testid="admin-category-tab-bar"
        >
          {categories.map((cat) => {
            const isActive = activeCategory === cat.slug;
            const count = collections.filter((c) => (c.category || 'limited-hair') === cat.slug).length;
            return (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`shrink-0 snap-start px-3 sm:px-4 py-2.5 text-xs sm:text-sm transition-all border-b-2 -mb-px flex items-center gap-1.5 ${
                  isActive
                    ? 'border-[#5D4E3C] text-[#5D4E3C] font-semibold bg-[#F5EDE4]/40'
                    : 'border-transparent text-[#9C8C7A] hover:text-[#5D4E3C]'
                }`}
                style={isActive ? { borderBottomColor: cat.accent_color || '#5D4E3C' } : {}}
                data-testid={`category-tab-${cat.slug}`}
              >
                <span className="text-base">{cat.emoji}</span>
                <span className="whitespace-nowrap">{cat.name.replace('Limited ', '')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F5EDE4] text-[#9C8C7A]">{count}</span>
                {cat.is_protected && <Lock className="h-3 w-3 opacity-50" />}
                {!cat.is_active && <span className="text-[9px] uppercase tracking-wider opacity-60">aus</span>}
              </button>
            );
          })}
        </div>
        {/* Mobile-Hinweis: alle Kategorien horizontal swipen */}
        <p className="text-[10px] text-[#9C8C7A] italic mt-[-12px] mb-3 sm:hidden">
          ← Wische horizontal für weitere Kategorien
        </p>

        {/* Active category info bar */}
        {activeCat && (
          <div className="bg-white border border-[#C4A084] p-3 sm:p-4 mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[#9C8C7A] uppercase tracking-wider">Aktive Kategorie</p>
              <p className="text-sm font-semibold text-[#5D4E3C]">
                {activeCat.emoji} {activeCat.name}
                {activeCat.tagline && <span className="text-[#9C8C7A] italic ml-2">— {activeCat.tagline}</span>}
              </p>
              {activeCat.description && (
                <p className="text-xs text-[#5C4E3D] mt-1 hidden sm:block">{activeCat.description}</p>
              )}
            </div>
            <div className="flex gap-2 shrink-0 self-end sm:self-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCategoryModal(activeCat)}
                data-testid="edit-category-btn"
              >
                <Pencil className="h-3.5 w-3.5 mr-1" /> <span className="hidden sm:inline">Kategorie </span>bearbeiten
              </Button>
              {!activeCat.is_protected && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteCategory(activeCat)}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="delete-category-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* PDF-Section-Header */}
        {activeCat && visibleCollections.length > 0 && (
          <p className="text-xs text-[#5D4E3C] bg-[#FAF5EE] border-l-4 border-[#C4A084] px-3 py-2 mb-3">
            📑 <strong>Etiketten-PDFs:</strong> Pro Edition unten findest du Buttons für Label-Bogen (A4) · Einzel-Etikett · ♡ Personalisiert.
          </p>
        )}

        {visibleCollections.length === 0 ? (
          <div className="bg-white p-10 border border-[#C4A084] text-center">
            <Sparkles className="h-12 w-12 mx-auto text-[#C4A084] mb-3" />
            <p className="text-[#5D4E3C] mb-4">
              Noch keine Kollektionen in {activeCat?.name || 'dieser Kategorie'}.
            </p>
            <div className="flex justify-center gap-2 flex-wrap">
              <Button onClick={startNew} className="bg-[#5D4E3C] text-white">
                <Plus className="h-4 w-4 mr-2" /> Neue Kollektion
              </Button>
              {activeCategory === 'limited-hair' && collections.length === 0 && (
                <Button variant="outline" onClick={seed}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Standard-Themen anlegen
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleCollections.map((c) => {
              const p = c.palette || EMPTY_COLLECTION.palette;
              const cat = c.category || 'limited-hair';
              const isCurated = cat !== 'limited-hair';
              const isSeasonal = cat === 'limited-seasonal';
              const bothType = isSeasonal ? 'both_seasonal' : isCurated ? 'both_study' : 'both';
              const smallType = isCurated ? 'small' : 'mini';
              const largeType = isSeasonal ? 'large' : isCurated ? 'medium' : 'classic';
              const smallLabel = isCurated ? 'Einzeln Small' : 'Einzeln Mini';
              const largeLabel = isSeasonal ? 'Einzeln Large' : isCurated ? 'Einzeln Medium' : 'Einzeln Classic';
              return (
                <div
                  key={c.slug}
                  className={`border overflow-hidden transition-opacity ${
                    c.is_active === false ? 'border-[#E8DCC8] opacity-70' : 'border-[#C4A084]'
                  }`}
                  data-testid={`hair-collection-card-${c.slug}`}
                >
                  <div className="p-5 relative" style={{ backgroundColor: p.bg, color: p.text }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] tracking-[3px] uppercase font-semibold" style={{ color: p.accent }}>
                          {c.style || 'Stil'}
                        </p>
                        <h3 className="text-xl font-bold mt-1" style={{ fontFamily: 'Georgia, serif' }}>
                          {c.emoji} {c.name}
                        </h3>
                        <p className="text-xs mt-1 opacity-80">{c.tagline}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        {c.is_active === false ? (
                          <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 border border-red-200 uppercase tracking-wider">
                            Inaktiv
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 border border-green-200 uppercase tracking-wider">
                            Aktiv
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleActive(c)}
                          title={c.is_active === false ? 'Aktivieren (im Shop sichtbar machen)' : 'Deaktivieren (im Shop verstecken)'}
                          aria-label={c.is_active === false ? `„${c.name}" aktivieren` : `„${c.name}" deaktivieren`}
                          className={`flex items-center justify-center h-7 w-7 rounded-full border transition-colors ${
                            c.is_active === false
                              ? 'bg-white border-[#C4A084] text-[#5D4E3C] hover:bg-[#C4A084] hover:text-white'
                              : 'bg-[#5D4E3C] border-[#5D4E3C] text-white hover:bg-[#4A3E30]'
                          }`}
                          data-testid={`hair-collection-toggle-${c.slug}`}
                        >
                          {c.is_active === false ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-white space-y-3">
                    <p className="text-xs text-[#8B7355]">
                      <strong className="text-[#5D4E3C]">{c.product_skus?.length || 0}</strong> verknüpfte Boxen
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`hair-collection-edit-${c.slug}`}>
                        Bearbeiten
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadLabel(c.slug, bothType, 'a4_sheet')} data-testid={`hair-collection-label-sheet-${c.slug}`}>
                        <Printer className="h-3.5 w-3.5 mr-1" /> Label-Bogen (A4)
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadLabel(c.slug, smallType, 'single')}>
                        {smallLabel}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadLabel(c.slug, largeType, 'single')}>
                        {largeLabel}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadPersonalisedLabel(c.slug, largeType)}
                        className="border-[#C4A084] text-[#5D4E3C]"
                        data-testid={`hair-collection-label-personalised-${c.slug}`}
                      >
                        ♡ Personalisiert
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c.slug)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 text-xs text-[#9C8C7A] italic">
          💡 Tipp: Lege oben neue Kategorien an (z.B. &bdquo;Saison Drops&ldquo;), und ordne dann Kollektionen einer Kategorie zu. Im Shop erscheinen Kategorien als Tabs.
        </div>

        {categoryModal && (
          <CategoryModal
            initial={categoryModal}
            onClose={() => setCategoryModal(null)}
            onSaved={(slug) => {
              setCategoryModal(null);
              setActiveCategory(slug);
              load();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default HairCollectionsManager;
